import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CalendarIcon, DollarSignIcon, RepeatIcon, ArrowRightIcon, SettingsIcon, ClockIcon } from 'lucide-react';
import { AnimatedScreen } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { getVRPFrequencyText } from '../../constants/uaeStandards';
import { VRPConfig } from './VRPSetupScreen';
export interface VRPSuccessScreenProps {
  merchantName: string;
  merchantLogo?: string;
  vrpConfig: VRPConfig;
  currency: string;
  nextPaymentDate?: string;
  onViewDashboard: () => void;
  onManageConsents?: () => void;
}
export function VRPSuccessScreen({
  merchantName,
  merchantLogo,
  vrpConfig,
  currency,
  nextPaymentDate,
  onViewDashboard,
  onManageConsents
}: VRPSuccessScreenProps) {
  // Calculate total value
  const totalValue = vrpConfig.maxAmountPerPayment * (vrpConfig.totalPaymentsAllowed || 12);
  // Calculate end date
  const calculateEndDate = () => {
    const date = new Date();
    const multiplier = vrpConfig.totalPaymentsAllowed || 12;
    switch (vrpConfig.frequency) {
      case 'monthly':
        date.setMonth(date.getMonth() + multiplier);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + multiplier * 3);
        break;
      case 'annual':
        date.setFullYear(date.getFullYear() + multiplier);
        break;
      default:
        date.setMonth(date.getMonth() + 12);
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  return <AnimatedScreen data-id="element-1458">
      <div className="space-y-6" data-id="element-1459">
        {/* Success Animation */}
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.1
      }} className="flex justify-center" data-id="element-1460">
          <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{
          background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.success} 0%, ${OPENFINANCE_THEME.colors.primary500} 100%)`,
          boxShadow: `0 10px 40px ${OPENFINANCE_THEME.colors.success}40`
        }} data-id="element-1461">
            <motion.div initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.3
          }} data-id="element-1462">
              <CheckCircle2Icon className="w-16 h-16 text-white" strokeWidth={2.5} data-id="element-1463" />
            </motion.div>
          </div>
        </motion.div>

        {/* Success Message */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="text-center" data-id="element-1464">
          <h2 className="text-3xl font-bold mb-2" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-1465">
            Auto-Pay Enabled! 🎉
          </h2>
          <p className="text-base leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-1466">
            Your {merchantName} bills will now be paid automatically
          </p>
        </motion.div>

        {/* Next Payment Highlight */}
        {nextPaymentDate && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="p-5 rounded-2xl text-center" style={{
        background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.success}15 0%, ${OPENFINANCE_THEME.colors.primary500}15 100%)`,
        border: `2px solid ${OPENFINANCE_THEME.colors.success}40`
      }} data-id="element-1467">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-1468">
              <CalendarIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-1469" />
              <span className="text-sm font-semibold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1470">
                Next Payment
              </span>
            </div>
            <div className="text-2xl font-bold" style={{
          color: OPENFINANCE_THEME.colors.success
        }} data-id="element-1471">
              {nextPaymentDate}
            </div>
            <div className="text-sm mt-1" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1472">
              Up to {currency} {vrpConfig.maxAmountPerPayment.toLocaleString()}
            </div>
          </motion.div>}

        {/* Payment Schedule Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="p-5 rounded-2xl border-2" style={{
        backgroundColor: 'white',
        borderColor: OPENFINANCE_THEME.colors.border
      }} data-id="element-1473">
          <div className="flex items-center gap-2 mb-4" data-id="element-1474">
            <RepeatIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1475" />
            <h3 className="text-lg font-bold" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1476">
              Payment Schedule
            </h3>
          </div>

          <div className="space-y-3" data-id="element-1477">
            <div className="flex justify-between items-center" data-id="element-1478">
              <span className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1479">
                Frequency
              </span>
              <span className="text-sm font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1480">
                {getVRPFrequencyText(vrpConfig.frequency)}
              </span>
            </div>

            <div className="flex justify-between items-center" data-id="element-1481">
              <span className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1482">
                Max per Payment
              </span>
              <span className="text-sm font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1483">
                {currency} {vrpConfig.maxAmountPerPayment.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center" data-id="element-1484">
              <span className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1485">
                Total Payments
              </span>
              <span className="text-sm font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1486">
                {vrpConfig.totalPaymentsAllowed || 12} payments
              </span>
            </div>

            <div className="flex justify-between items-center" data-id="element-1487">
              <span className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1488">
                Auto-pay until
              </span>
              <span className="text-sm font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1489">
                {calculateEndDate()}
              </span>
            </div>

            <div className="pt-3 mt-3 border-t flex justify-between items-center" style={{
            borderColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1490">
              <span className="text-base font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1491">
                Total Value Allowed
              </span>
              <span className="text-xl font-bold" style={{
              color: OPENFINANCE_THEME.colors.primary700
            }} data-id="element-1492">
                {currency} {totalValue.toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Merchant Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="p-5 border-2 rounded-2xl" style={{
        background: OPENFINANCE_THEME.gradients.card,
        borderColor: OPENFINANCE_THEME.colors.primary200
      }} data-id="element-1493">
          <div className="flex items-center gap-3" data-id="element-1494">
            {merchantLogo && <div className="w-14 h-14 rounded-xl flex items-center justify-center p-2" style={{
            backgroundColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1495">
                <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-1496" />
              </div>}
            <div className="flex-1" data-id="element-1497">
              <div className="font-bold text-lg" style={{
              color: OPENFINANCE_THEME.colors.text,
              fontFamily: OPENFINANCE_THEME.fonts.heading
            }} data-id="element-1498">
                {merchantName}
              </div>
              <div className="text-sm flex items-center gap-1" style={{
              color: OPENFINANCE_THEME.colors.success
            }} data-id="element-1499">
                <CheckCircle2Icon className="w-4 h-4" data-id="element-1500" />
                <span className="font-semibold" data-id="element-1501">Auto-pay active</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* What Happens Next */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="p-5 rounded-2xl" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`
      }} data-id="element-1502">
          <div className="text-sm font-bold mb-3" style={{
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-1503">
            What Happens Next
          </div>
          <ul className="space-y-2" data-id="element-1504">
            {['Payments will be processed automatically on the due date', "You'll receive a notification before each payment", 'View payment history anytime in Consent Management', 'Pause or cancel auto-pay whenever you want'].map((item, index) => <motion.li key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.9 + index * 0.1
          }} className="flex items-start gap-2 text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-1505">
                <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
              color: OPENFINANCE_THEME.colors.success
            }} data-id="element-1506" />
                <span data-id="element-1507">{item}</span>
              </motion.li>)}
          </ul>
        </motion.div>

        {/* Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2
      }} className="space-y-3 pt-2" data-id="element-1508">
          {/* Manage Consents - Primary Action */}
          {onManageConsents && <button onClick={onManageConsents} className="w-full px-6 py-4 rounded-full font-bold text-white transition-all hover:shadow-lg flex items-center justify-center gap-2" style={{
          background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`
        }} data-id="element-1509">
              <SettingsIcon className="w-5 h-5" data-id="element-1510" />
              <span data-id="element-1511">Manage Auto-Pay Settings</span>
            </button>}

          {/* Back to Dashboard - Secondary Action */}
          <button onClick={onViewDashboard} className="w-full px-6 py-4 rounded-full font-semibold transition-all border-2 hover:bg-gray-50 flex items-center justify-center gap-2" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          color: OPENFINANCE_THEME.colors.text
        }} data-id="element-1512">
            <span data-id="element-1513">Back to Dashboard</span>
            <ArrowRightIcon className="w-5 h-5" data-id="element-1514" />
          </button>
        </motion.div>

        {/* Celebration Confetti Effect */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 1, 0]
      }} transition={{
        duration: 2,
        delay: 0.2
      }} className="fixed inset-0 pointer-events-none flex items-center justify-center" style={{
        zIndex: 9999
      }} data-id="element-1515">
          {[...Array(20)].map((_, i) => <motion.div key={i} initial={{
          x: 0,
          y: 0,
          scale: 0,
          rotate: 0
        }} animate={{
          x: (Math.random() - 0.5) * 400,
          y: (Math.random() - 0.5) * 400,
          scale: [0, 1, 0],
          rotate: Math.random() * 360
        }} transition={{
          duration: 1.5,
          delay: Math.random() * 0.3,
          ease: 'easeOut'
        }} className="absolute w-3 h-3 rounded-full" style={{
          backgroundColor: [OPENFINANCE_THEME.colors.primary500, OPENFINANCE_THEME.colors.accent500, OPENFINANCE_THEME.colors.success, '#FFD700'][i % 4]
        }} data-id="element-1516" />)}
        </motion.div>
      </div>
    </AnimatedScreen>;
}